import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, Ref } from 'vue';
import {
  loadResourceEventsByDate,
  loadResourceEventsByDateForCurrentUser,
  updateResourceEvent,
} from '/@stores/resources';
import type { ResourceEvent } from '/@types/resources';

export const queryKeys = {
  all: ['resourceEvents'] as const,
  user: (start: string, end: string) => [...queryKeys.all, 'user', { start, end }] as const,
  events: (start: string, end: string) => [...queryKeys.all, 'events', { start, end }] as const,
};

export function useResourceEventsUserQuery(dateRange: Ref<{ start: Date; end: Date } | null>) {
  return useQuery<ResourceEvent[]>({
    queryKey: computed(() =>
      queryKeys.user(
        dateRange.value?.start.toISOString() ?? '',
        dateRange.value?.end.toISOString() ?? '',
      ),
    ),
    queryFn: () =>
      loadResourceEventsByDateForCurrentUser(dateRange.value!.start, dateRange.value!.end),
    enabled: () => !!dateRange.value,
    placeholderData: (prev) => prev,
  });
}

export function useResourceEventsAllQuery(dateRange: Ref<{ start: Date; end: Date } | null>) {
  return useQuery<ResourceEvent[]>({
    queryKey: computed(() =>
      queryKeys.events(
        dateRange.value?.start.toISOString() ?? '',
        dateRange.value?.end.toISOString() ?? '',
      ),
    ),
    queryFn: () => loadResourceEventsByDate(dateRange.value!.start, dateRange.value!.end),
    enabled: () => !!dateRange.value,
    placeholderData: (prev) => prev,
  });
}

export function useUpdateResourceEventMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ guid, patch }: { guid: string; patch: ResourceEvent }) =>
      updateResourceEvent(guid, {
        resourceGuid: patch.resourceGuid,
        resourceType: patch.resourceType,
        projectId: patch.projectId,
        fromDate: patch.fromDate,
        toDate: patch.toDate,
        comment: patch.comment ?? '',
        users: [],
        isHighlighted: patch.isHighlighted,
      }),
    onMutate: async ({ guid, patch }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.all });
      const previousEntries = queryClient.getQueriesData<ResourceEvent[]>({
        queryKey: queryKeys.all,
      });

      queryClient.setQueriesData<ResourceEvent[]>({ queryKey: queryKeys.all }, (old) =>
        old?.map((event) => (event.guid === guid ? { ...event, ...patch } : event)),
      );

      return { previousEntries };
    },
    onError: (_err, _vars, context) => {
      context?.previousEntries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
}
